import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import iconSpotify from '../assets/icon-spotify.svg';

const EMPTY_RELEASE = {
  artist: '',
  album: '',
  genre: '',
  releaseDate: '',
  can: false,
  local: false
};

const COLUMN_KEYS = [
  { key: 'artist', header: 'artist' },
  { key: 'album', header: 'album' },
  { key: 'genre', header: 'genre' },
  { key: 'releaseDate', header: 'releasedate' },
  { key: 'can', header: 'can' },
  { key: 'local', header: 'local' }
];

const GENRE_OPTIONS = [
  'Alternative',
  'Alternative Country',
  'Alternative Hip-Hop',
  'Alternative R&B',
  'Ambient',
  'Blues',
  'Blues Rock',
  'Classical',
  'Country',
  'Country Rock',
  'Dance',
  'Dance-Pop',
  'Dream Pop',
  'Electronic',
  'Electro-Pop',
  'Experimental',
  'Experimental Pop',
  'Experimental Rock',
  'Folk',
  'Folk Rock',
  'Funk',
  'Garage',
  'Garage Rock',
  'Gospel',
  'Hip-Hop',
  'House',
  'Indie',
  'Indie Folk',
  'Indie Pop',
  'Indie Rock',
  'Jazz',
  'Jazz Fusion',
  'Latin',
  'Metal',
  'Noise',
  'Pop',
  'Pop-Punk',
  'Pop-Rock',
  'Post-Punk',
  'Post-Rock',
  'Punk',
  'R&B',
  'Rap',
  'Reggae',
  'Roots',
  'Rock',
  'Singer-Songwriter',
  'Shoegaze',
  'Soul',
  'Soundtrack',
  'Synth-Pop',
  'Techno',
  'World'
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function normalizeBoolean(value) {
  if (typeof value === 'boolean') return value;
  const normalized = String(value || '').trim().toLowerCase();
  return ['yes', 'y', 'true', '1', 'can', 'canadian', 'local'].includes(normalized);
}

function normalizeRelease(release) {
  const releaseDate = release.releaseDate || release.added || '';

  return {
    artist: String(release.artist || '').trim(),
    album: String(release.album || '').trim(),
    genre: String(release.genre || '').trim(),
    releaseDate,
    can: normalizeBoolean(release.can),
    local: normalizeBoolean(release.local)
  };
}

function getSearchAlbumTitle(album) {
  return String(album || '')
    .replace(/\s*[-–—:()[]?\s*(ep|single|lp|album|deluxe|remaster(ed)?|remix)\s*[\])]?$/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function getSpotifyAlbumSearchUrl(release) {
  const query = `${getSearchAlbumTitle(release.album)} ${release.artist}`.trim();
  return `https://open.spotify.com/search/${encodeURIComponent(query)}/albums`;
}

function getAppleMusicAlbumSearchUrl(release) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '/api';
  const params = new URLSearchParams({
    artist: release.artist,
    album: getSearchAlbumTitle(release.album)
  });

  return `${backendUrl}/apple-music-link?${params.toString()}`;
}

function getReleaseKey(release) {
  return `${release.artist}|${release.album}|${release.releaseDate}`;
}

function escapeCsvValue(value) {
  const stringValue = String(value ?? '');
  if (/[",\n\r]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

function parseDelimitedText(text) {
  const rows = [];
  let current = '';
  let row = [];
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];

    if (char === '"' && next === '"') {
      current += '"';
      index += 1;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if ((char === ',' || char === '\t') && !inQuotes) {
      row.push(current.trim());
      current = '';
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && next === '\n') index += 1;
      row.push(current.trim());
      if (row.some(Boolean)) rows.push(row);
      row = [];
      current = '';
    } else {
      current += char;
    }
  }

  row.push(current.trim());
  if (row.some(Boolean)) rows.push(row);

  if (rows.length === 0) return [];

  const headers = rows[0].map((header) => header.toLowerCase().replace(/[^a-z]/g, ''));
  const hasHeader = ['artist', 'album', 'genre'].some((key) => headers.includes(key));
  const dataRows = hasHeader ? rows.slice(1) : rows;

  return dataRows
    .map((cells) => {
      const source = {};

      COLUMN_KEYS.forEach(({ key, header }, index) => {
        const headerIndex = headers.indexOf(header);
        source[key] = cells[hasHeader && headerIndex >= 0 ? headerIndex : index];
      });

      return normalizeRelease(source);
    })
    .filter((release) => release.artist && release.album);
}

function NewReleasesPage() {
  const { accounts } = useMsal();
  const [releases, setReleases] = useState([]);
  const [form, setForm] = useState(EMPTY_RELEASE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [genreFilter, setGenreFilter] = useState('');
  const [canFilter, setCanFilter] = useState('all');
  const [localFilter, setLocalFilter] = useState('all');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const fileInputRef = useRef(null);

  const isAuthenticated = accounts.length > 0;

  const sortedReleases = useMemo(() => (
    [...releases].sort((a, b) => new Date(b.releaseDate) - new Date(a.releaseDate))
  ), [releases]);

  const filteredReleases = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return sortedReleases.filter((release) => {
      const searchableText = [
        release.artist,
        release.album,
        release.genre,
        release.can ? 'can canadian' : '',
        release.local ? 'local' : ''
      ].join(' ').toLowerCase();

      const matchesSearch = !normalizedSearch || searchableText.includes(normalizedSearch);
      const matchesGenre = !genreFilter || release.genre === genreFilter;
      const matchesCan = canFilter === 'all' || release.can === (canFilter === 'yes');
      const matchesLocal = localFilter === 'all' || release.local === (localFilter === 'yes');

      return matchesSearch && matchesGenre && matchesCan && matchesLocal;
    });
  }, [canFilter, genreFilter, localFilter, searchTerm, sortedReleases]);

  const totalPages = Math.max(1, Math.ceil(filteredReleases.length / pageSize));
  const clampedPage = Math.min(currentPage, totalPages);
  const pageStart = (clampedPage - 1) * pageSize;
  const pagedReleases = filteredReleases.slice(pageStart, pageStart + pageSize);

  const genreOptions = useMemo(() => {
    const importedGenres = releases
      .map((release) => release.genre)
      .filter(Boolean);

    return [...new Set([...GENRE_OPTIONS, ...importedGenres])].sort((a, b) => a.localeCompare(b));
  }, [releases]);

  const loadReleases = useCallback(async () => {
    setLoading(true);
    setMessage('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '/api';
      let response = await fetch(`${backendUrl}/new-releases`, { cache: 'no-store' });

      if (!response.ok) {
        response = await fetch('/new-releases.json', { cache: 'no-store' });
      }

      if (!response.ok) {
        throw new Error('No public releases file yet');
      }

      const data = await response.json();
      setReleases((data.releases || []).map(normalizeRelease));
    } catch (error) {
      setReleases([]);
      setMessage('No new releases list is published yet.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReleases();
  }, [loadReleases]);

  useEffect(() => {
    setCurrentPage(1);
  }, [canFilter, genreFilter, localFilter, pageSize, searchTerm]);

  const saveReleases = async (nextReleases, successMessage) => {
    setSaving(true);
    setMessage('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '/api';
      const response = await fetch(`${backendUrl}/new-releases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          releases: nextReleases,
          updatedBy: accounts[0]?.name || 'Unknown'
        })
      });

      if (!response.ok) {
        let errorMessage = `Backend returned ${response.status}`;

        try {
          const errorData = await response.json();
          if (errorData?.error) {
            errorMessage = errorData.error;
          }
        } catch (parseError) {
          errorMessage = `Backend returned ${response.status}`;
        }

        throw new Error(errorMessage);
      }

      setReleases(nextReleases);
      setMessage(successMessage);
    } catch (error) {
      setMessage(`Could not update new-releases.json: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = (event) => {
    const { name, value, checked, type } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextRelease = normalizeRelease(form);

    if (!nextRelease.artist || !nextRelease.album || !nextRelease.releaseDate) {
      setMessage('Artist, album, and release date are required.');
      return;
    }

    await saveReleases([nextRelease, ...releases], 'New release added.');
    setForm(EMPTY_RELEASE);
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const lowerName = file.name.toLowerCase();
    if (lowerName.endsWith('.xlsx')) {
      setMessage('XLSX workbooks need an Excel parser package. Please export as CSV for this version.');
      event.target.value = '';
      return;
    }

    const text = await file.text();
    const importedReleases = parseDelimitedText(text);

    if (importedReleases.length === 0) {
      setMessage('No releases found. Use columns: Artist, Album, Genre, Release Date, Can?, Local?.');
      event.target.value = '';
      return;
    }

    await saveReleases(
      [...importedReleases, ...releases],
      `${importedReleases.length} release${importedReleases.length === 1 ? '' : 's'} imported.`
    );
    event.target.value = '';
  };

  const handleDelete = async (releaseToDelete) => {
    const shouldDelete = window.confirm(`Delete "${releaseToDelete.album}" by ${releaseToDelete.artist}?`);
    if (!shouldDelete) return;

    const deleteKey = getReleaseKey(releaseToDelete);
    const nextReleases = releases.filter((release) => getReleaseKey(release) !== deleteKey);

    await saveReleases(nextReleases, 'Release deleted.');
  };

  const resetFilters = () => {
    setSearchTerm('');
    setGenreFilter('');
    setCanFilter('all');
    setLocalFilter('all');
    setPageSize(10);
    setCurrentPage(1);
  };

  const handleExport = () => {
    const headers = ['Artist', 'Album', 'Genre', 'Release Date', 'Can?', 'Local?', 'Spotify', 'Apple Music'];
    const rows = filteredReleases.map((release) => [
      release.artist,
      release.album,
      release.genre,
      formatDate(release.releaseDate),
      release.can ? 'Yes' : 'No',
      release.local ? 'Yes' : 'No',
      getSpotifyAlbumSearchUrl(release),
      getAppleMusicAlbumSearchUrl(release)
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map(escapeCsvValue).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `cfmu-new-releases-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return `${month}/${day}/${year}`;
    }

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'Unknown';

    return date.toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <main className="main-content releases-page">
      <section className="releases-hero">
        <div className="releases-hero-inner">
          <a href="/" className="back-link">Back Home</a>
          <div>
            <p className="section-kicker">CFMU Music Library</p>
            <h1>New Releases</h1>
          </div>
          <p className="releases-intro">
            A public shelf for fresh albums coming through the station, styled for quick scans by programmers,
            hosts, and community listeners.
          </p>
        </div>
      </section>

      <section className="releases-workspace">
        {isAuthenticated ? (
          <form className="release-form" onSubmit={handleSubmit}>
            <div className="release-form-header">
              <div>
                <p className="section-kicker">Staff Entry</p>
                <h2>Add a Release</h2>
              </div>
              <div className="release-actions">
                <input
                  ref={fileInputRef}
                  className="release-file-input"
                  type="file"
                  accept=".csv,.tsv,.txt,.xls,.xlsx"
                  onChange={handleImport}
                />
                <button
                  className="secondary-action"
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={saving}
                >
                  Import
                </button>
                <button className="primary-action" type="submit" disabled={saving}>
                  {saving ? 'Saving...' : 'Add Release'}
                </button>
              </div>
            </div>

            <div className="release-fields">
              <label>
                <span>Artist</span>
                <input name="artist" value={form.artist} onChange={handleFormChange} required />
              </label>
              <label>
                <span>Album</span>
                <input name="album" value={form.album} onChange={handleFormChange} required />
              </label>
              <label>
                <span>Genre</span>
                <select name="genre" value={form.genre} onChange={handleFormChange}>
                  <option value="">Select genre</option>
                  {genreOptions.map((genre) => (
                    <option key={genre} value={genre}>{genre}</option>
                  ))}
                </select>
              </label>
              <div className="release-meta-row">
                <label className="release-date-field">
                  <span>Release Date</span>
                  <input name="releaseDate" type="date" value={form.releaseDate} onChange={handleFormChange} required />
                </label>
                <div className="release-checks">
                <label className="release-check">
                  <input name="can" type="checkbox" checked={form.can} onChange={handleFormChange} />
                  <span>Can?</span>
                </label>
                <label className="release-check">
                  <input name="local" type="checkbox" checked={form.local} onChange={handleFormChange} />
                  <span>Local?</span>
                </label>
                </div>
              </div>
            </div>
          </form>
        ) : null}

        {message && <div className="release-message">{message}</div>}

        <div className="release-list-shell">
          <div className="release-list-header">
            <div>
              <p className="section-kicker">Library List</p>
              <h2>New Releases</h2>
            </div>
            <button className="secondary-action compact" type="button" onClick={loadReleases} disabled={loading}>
              {loading ? 'Loading' : 'Reload'}
            </button>
          </div>

          <div className="release-list-controls">
            <label className="release-search">
              <span>Search</span>
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Artist, album, genre"
              />
            </label>

            <label>
              <span>Genre</span>
              <select value={genreFilter} onChange={(event) => setGenreFilter(event.target.value)}>
                <option value="">All genres</option>
                {genreOptions.map((genre) => (
                  <option key={genre} value={genre}>{genre}</option>
                ))}
              </select>
            </label>

            <label>
              <span>Can?</span>
              <select value={canFilter} onChange={(event) => setCanFilter(event.target.value)}>
                <option value="all">All</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>

            <label>
              <span>Local?</span>
              <select value={localFilter} onChange={(event) => setLocalFilter(event.target.value)}>
                <option value="all">All</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>

            <label>
              <span>Show</span>
              <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>

            <button className="filter-reset-button" type="button" onClick={resetFilters}>
              Reset
            </button>
            <button
              className="filter-export-button"
              type="button"
              onClick={handleExport}
              disabled={filteredReleases.length === 0}
            >
              Export CSV
            </button>
          </div>

          <div className="release-table-wrap">
            <table className="release-table">
              <thead>
                <tr>
                  <th>Artist</th>
                  <th>Album</th>
                  <th>Genre</th>
                  <th>Can?</th>
                  <th>Local?</th>
                  <th>Release Date</th>
                  <th>Listen</th>
                  {isAuthenticated && <th>Delete</th>}
                </tr>
              </thead>
              <tbody>
                {pagedReleases.map((release, index) => (
                  <tr key={`${release.artist}-${release.album}-${release.releaseDate}-${index}`}>
                    <td data-label="Artist">{release.artist}</td>
                    <td data-label="Album">{release.album}</td>
                    <td data-label="Genre">{release.genre || 'Unlisted'}</td>
                    <td data-label="Can?">
                      <span className={release.can ? 'release-badge yes' : 'release-badge'}>{release.can ? 'Yes' : 'No'}</span>
                    </td>
                    <td data-label="Local?">
                      <span className={release.local ? 'release-badge yes' : 'release-badge'}>{release.local ? 'Yes' : 'No'}</span>
                    </td>
                    <td data-label="Release Date">{formatDate(release.releaseDate)}</td>
                    <td data-label="Listen">
                      <div className="release-listen-links">
                      <a
                        className="spotify-release-link"
                        href={getSpotifyAlbumSearchUrl(release)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Search Spotify for ${release.album} by ${release.artist}`}
                        aria-label={`Search Spotify for ${release.album} by ${release.artist}`}
                      >
                        <img src={iconSpotify} alt="" />
                      </a>
                      <a
                        className="apple-music-release-link"
                        href={getAppleMusicAlbumSearchUrl(release)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Search Apple Music for ${release.album} by ${release.artist}`}
                        aria-label={`Search Apple Music for ${release.album} by ${release.artist}`}
                      >
                        AM
                      </a>
                      </div>
                    </td>
                    {isAuthenticated && (
                      <td data-label="Delete">
                        <button
                          className="release-delete-button"
                          type="button"
                          onClick={() => handleDelete(release)}
                          disabled={saving}
                          title={`Delete ${release.album}`}
                        >
                          Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredReleases.length === 0 && (
              <div className="release-empty-state">
                No releases match those filters.
              </div>
            )}
          </div>

          <div className="release-pagination">
            <span>
              Showing {filteredReleases.length === 0 ? 0 : pageStart + 1}
              {'-'}
              {Math.min(pageStart + pageSize, filteredReleases.length)} of {filteredReleases.length}
            </span>
            <div className="pagination-buttons">
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={clampedPage <= 1}
              >
                Previous
              </button>
              <span>Page {clampedPage} of {totalPages}</span>
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                disabled={clampedPage >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default NewReleasesPage;
